import React, { useCallback } from 'react'
import styled, { useTheme } from 'styled-components'
import { Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { PlusCircle, RefreshCw } from 'react-feather'
import useToast from 'hooks/useToast'
import type { WalletPanelBalanceRow } from 'hooks/useWalletPanelOnchainBalances'

interface WalletPanelTokenRowsProps {
  rows: WalletPanelBalanceRow[]
  /** Offered on a row whose read failed, so "—" is not a dead end. */
  onRetry?: () => void
}

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 56px;
  padding: 0 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`

const TokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1;
`

const TokenLabel = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
`

const TokenSymbol = styled(Text)`
  flex-shrink: 0;
`

const ChainLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.gray2};
  font-size: 50%;
  line-height: 1;
`

const TokenIcon = styled.img`
  display: inline-flex;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  object-fit: cover;
`

const Amount = styled(Text)`
  flex-shrink: 0;
  text-align: right;
`

const EmptyState = styled.div`
  padding: 12px 0;
`

/*
 * A column of its own between the token and its value. Quiet by design: this
 * is a convenience for the few people whose wallet does not yet know MPRO, so
 * it should be findable without competing with the balance — which is what
 * the row is actually for.
 */
const IconAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray7};
  cursor: pointer;
  transition: color 120ms ease;
  &:hover {
    color: ${({ theme }) => theme.colors.purple};
  }
`

/** Keeps the value column aligned when a row has no action to offer. */
const IconSlot = styled.div`
  width: 26px;
  flex-shrink: 0;
`

const WalletPanelTokenRows: React.FC<WalletPanelTokenRowsProps> = ({ rows, onRetry }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { toastInfo, toastSuccess } = useToast()

  /**
   * Ask the wallet to display the token. MPRO Lab is where people manage MPRO,
   * so a balance shown here that is invisible in their wallet is a gap the
   * platform can close in one click — `wallet_watchAsset` is the standard way
   * and the wallet still asks the user before adding anything.
   */
  const addToWallet = useCallback(
    async (row: WalletPanelBalanceRow) => {
      const provider = (window as unknown as { ethereum?: { request: (a: unknown) => Promise<unknown> } }).ethereum
      if (!provider || !row.tokenAddress) return

      /*
       * Say something before the wallet is asked. MetaMask does not always
       * raise its own window — when it cannot take focus the request simply
       * queues behind the extension icon, so from here the click looked dead
       * and the feature read as broken when it was working the whole time.
       */
      toastInfo(t('walletPanel.checkYourWallet'))
      try {
        const added = await provider.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options: {
              address: row.tokenAddress,
              symbol: row.symbol,
              decimals: 18,
              image: `${window.location.origin}${row.iconSrc}`
            }
          }
        })
        if (added) toastSuccess(t('walletPanel.tokenAdded', { symbol: row.symbol }))
      } catch {
        // The user declining is the ordinary outcome, not a fault to report.
      }
    },
    [t, toastInfo, toastSuccess]
  )

  if (!rows.length) {
    return (
      <EmptyState>
        <Text variant={TextVariant.BODY_SMALL}>No balances to display.</Text>
      </EmptyState>
    )
  }

  return (
    <Rows>
      {rows.map((row) => (
        <Row key={row.id}>
          <TokenInfo>
            <TokenIcon src={row.iconSrc} alt={`${row.symbol} icon`} />
            <TokenLabel>
              <TokenSymbol variant={TextVariant.BODY_DEFAULT}>{row.symbol}</TokenSymbol>
              <ChainLabel variant={TextVariant.BODY_DEFAULT}>({row.chainLabel})</ChainLabel>
            </TokenLabel>
          </TokenInfo>
          {/*
            A token the wallet does not know about shows nothing there, even
            when the balance is real. Offered per chain, because the wallet has
            to be on that network for the entry to mean anything.
          */}
          {row.failed && onRetry ? (
            <IconAction
              type="button"
              onClick={onRetry}
              title={t('walletPanel.retry')}
              aria-label={t('walletPanel.retry')}
            >
              <RefreshCw size={15} />
            </IconAction>
          ) : row.tokenAddress ? (
            <IconAction
              type="button"
              onClick={() => void addToWallet(row)}
              title={t('walletPanel.addToWallet')}
              aria-label={t('walletPanel.addToWallet')}
            >
              <PlusCircle size={15} />
            </IconAction>
          ) : (
            <IconSlot />
          )}
          <Amount
            variant={TextVariant.BODY_DEFAULT}
            color={row.failed ? colors.gray6 : undefined}
            title={row.failed ? t('walletPanel.balanceUnreadable') : undefined}
          >
            {row.amountDisplay}
          </Amount>
        </Row>
      ))}
    </Rows>
  )
}

export default WalletPanelTokenRows
